import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Link } from '../../utils/router/v3Bridge';
import CheckRadioControl from '../../components/formInternals/CheckRadioControl';
import SVGIcon from '../../components/SVGIcon';
import dragIcon from '../../icons/drag.svg';
import { DragTypes } from '../useTopicReorganization';
import styles from './TopicTreeCardItemDraggable.module.scss';

const BaseTopicTreeCardItemDraggable = ({
  to,
  type,
  children,
  dragItem,
  onCheckboxChange,
  reorganizable,
  isChecked,
  isCheckboxDisabled,
  isReorganizing,
  isMovedOut,
}) => {
  const canDrag = isReorganizing && !isMovedOut && reorganizable;
  const dragSpec = useMemo(
    () => ({
      type: dragItem.type,
      item: dragItem,
      canDrag,
      monitor: () => null,
    }),
    [canDrag, dragItem],
  );
  const [, dragRef, preview] = useDrag(dragSpec);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);
  const Component = isReorganizing ? 'label' : 'div';
  const isDisabled = isMovedOut || isCheckboxDisabled || !reorganizable;

  return (
    <Component
      className={classNames(styles.container, styles[type], {
        [styles.movedOut]: isMovedOut,
        [styles.draggable]: canDrag,
      })}
      ref={dragRef}
    >
      <div className={styles.dragIconAndTitle}>
        <div className={styles.dragIconContainer}>
          <SVGIcon className={styles.dragIcon} size={10} coloringWith="fill">
            {dragIcon}
          </SVGIcon>
        </div>
        {isReorganizing && (
          <div className={styles.checkboxContainer}>
            <CheckRadioControl
              type="checkbox"
              disabled={isDisabled}
              onChange={onCheckboxChange}
              checked={!isMovedOut && isChecked}
            />
          </div>
        )}
        <Link
          className={styles.title}
          to={to}
          target={type === 'topic' ? undefined : '_blank'}
          onClick={(e) => {
            if (!isReorganizing || e.metaKey) return;
            e.preventDefault();
            if (!isDisabled) {
              onCheckboxChange(!isChecked);
            }
          }}
        >
          {children}
        </Link>
      </div>
    </Component>
  );
};

BaseTopicTreeCardItemDraggable.propTypes = {
  to: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  dragItem: PropTypes.shape({
    type: PropTypes.string.isRequired,
    toMove: PropTypes.shape({}).isRequired,
  }).isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  reorganizable: PropTypes.bool.isRequired,
  isChecked: PropTypes.bool.isRequired,
  isCheckboxDisabled: PropTypes.bool.isRequired,
  isReorganizing: PropTypes.bool.isRequired,
  isMovedOut: PropTypes.bool.isRequired,
};

const propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  reorganizable: PropTypes.bool.isRequired,
  isChecked: PropTypes.bool.isRequired,
  isCheckboxDisabled: PropTypes.bool.isRequired,
  isReorganizing: PropTypes.bool.isRequired,
  isMovedOut: PropTypes.bool.isRequired,
  toMove: PropTypes.shape({
    topicIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    postKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
    pinnedPostKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  type: PropTypes.oneOf(['post', 'topic']).isRequired,
};

const TopicTreeCardItemDraggable = ({
  to,
  children,
  onCheckboxChange,
  reorganizable,
  isChecked,
  isCheckboxDisabled,
  isReorganizing,
  isMovedOut,
  toMove,
  type,
}) => {
  const dragItem = {
    toMove,
  };
  if (
    toMove.topicIds.length > 0 &&
    toMove.postKeys.length === 0 &&
    toMove.pinnedPostKeys.length === 0
  ) {
    dragItem.type = DragTypes.topics;
  } else if (
    toMove.topicIds.length === 0 &&
    toMove.postKeys.length > 0 &&
    toMove.pinnedPostKeys.length === 0
  ) {
    dragItem.type = DragTypes.posts;
  } else if (
    toMove.topicIds.length === 0 &&
    toMove.postKeys.length === 0 &&
    toMove.pinnedPostKeys.length > 0
  ) {
    dragItem.type = DragTypes.pinnedPosts;
  } else {
    dragItem.type = DragTypes.mixed;
  }

  return (
    <BaseTopicTreeCardItemDraggable
      to={to}
      type={type}
      dragItem={dragItem}
      onCheckboxChange={onCheckboxChange}
      reorganizable={reorganizable}
      isChecked={isChecked}
      isCheckboxDisabled={isCheckboxDisabled}
      isReorganizing={isReorganizing}
      isMovedOut={isMovedOut}
    >
      {children}
    </BaseTopicTreeCardItemDraggable>
  );
};

TopicTreeCardItemDraggable.propTypes = propTypes;

export default TopicTreeCardItemDraggable;
